import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, Breadcrumbs, useTheme,
} from "@mui/material";

import ReceiptIcon from "@mui/icons-material/Receipt";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SendIcon from "@mui/icons-material/Send";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import PersonIcon from "@mui/icons-material/Person";
import TagIcon from "@mui/icons-material/Tag";
import CategoryIcon from "@mui/icons-material/Category";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DiscountIcon from "@mui/icons-material/Discount";
import BadgeIcon from "@mui/icons-material/Badge";

import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { Cut, Line, Printer, Text, Br, Row, Image, render } from "react-thermal-printer";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import { requester } from "api/pesabackend";
import NewDialog from "components/newDialog";
import ReceiptConfirmView from "./receiptConfirmView";
import InvoiceCreateView from "./invoiceCreateView";
import SalesInvoicePdf from "../forms/invoicePdf";
import SalesReceiptPdf from "../forms/recieptPdf";
import SalesBillPdf from "../forms/billPdf";
import JolilabsLogo from "../../../components/Logo/jolilabs_logo_pos.png";
import { FormProviderCreateReceipt } from "../forms/formSetups/receiptCreateForm";
import { currencyFormatter, withoutSymbolFormatter } from "utilities/currencyFormatter";

/* ── Detail field ── */
const DetailField = ({ icon, label, value, textPri, textSec, accent }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", py: "0.6rem", borderBottom: "0.5px solid rgba(29,158,117,0.08)" }}>
    <Box sx={{ color: accent, flexShrink: 0, mt: "2px", opacity: 0.7 }}>
      {React.cloneElement(icon, { sx: { fontSize: "16px" } })}
    </Box>
    <Box>
      <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec, mb: "2px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "13px", color: textPri }}>
        {value != null && value !== "" ? value : "—"}
      </Typography>
    </Box>
  </Box>
);

/* ── Status pill ── */
const StatusPill = ({ status }) => {
  const config = status === true
    ? { label: "Paid", bg: "rgba(29,158,117,0.12)", color: "#1D9E75" }
    : status === false
      ? { label: "Unpaid", bg: "rgba(226,75,74,0.1)", color: "#e24b4a" }
      : { label: "Unknown", bg: "rgba(136,135,128,0.12)", color: "#888780" };
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "8px", py: "3px", borderRadius: "20px", backgroundColor: config.bg }}>
      <Box sx={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: config.color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: config.color, lineHeight: 1 }}>{config.label}</Typography>
    </Box>
  );
};

/* ── Action button ── */
const ActionBtn = ({ icon, onClick, variant = "primary", disabled = false, children }) => {
  const styles = {
    primary: { bg: "#1D9E75", hover: "#0F6E56", color: "#fff", border: "none" },
    ghost: { bg: "transparent", hover: "rgba(29,158,117,0.08)", color: "#1D9E75", border: "1px solid rgba(29,158,117,0.3)" },
  };
  const s = styles[variant];
  return (
    <Button onClick={onClick} disabled={disabled} startIcon={icon}
      sx={{
        fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.5rem",
        borderRadius: "8px", textTransform: "none",
        backgroundColor: s.bg, color: s.color, border: s.border,
        width: "100%", justifyContent: "flex-start",
        "&:hover": { backgroundColor: s.hover },
        "&:disabled": { opacity: 0.5 },
      }}
    >
      {children}
    </Button>
  );
};

const InvoiceDetailsView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const user = useSelector((s) => s.authReducer.user);

  const bodyBg = theme.palette.background.alt;
  const cardBg = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const { id } = location.state;

  /* ── State ── */
  const [invoiceDetails, setInvoiceDetails] = useState({});
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [openInvoice, setOpenInvoice] = useState(false);
  const [amountPaid] = useState(0);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentModesUsed, setPaymentModesUsed] = useState("");
  const [isValidated, setIsValidated] = useState(false);
  const [isValidatedInvoice, setIsValidatedInvoice] = useState(false);
  const [isSubmittingReceipt, setIsSubmittingReceipt] = useState(false);
  const [isSubmittingInvoice, setIsSubmittingInvoice] = useState(false);
  const [multiplePayments, setMultiplePayments] = useState(false);
  const [receiptDate, setReceiptDate] = useState(moment().format("YYYY-MM-DD HH:mm"));

  /* ── Data fetching ── */
  const fetchInvoiceDetails = useCallback(async () => {
    try {
      const res = await requester.get(`/billing/invoices/details?id=${ id }`);
      setInvoiceDetails(res.data.data);
      setProducts(res.data.data.products);
      setCustomer(res.data.data.related_customer ? res.data.data.related_customer.id : "");
      setIsValidatedInvoice(!!res.data.data.related_customer);
    } catch { }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchInvoiceDetails(); }, [fetchInvoiceDetails]);

  const fetchPaymentTypes = async () => {
    try {
      const res = await requester.get(`/billing/payment_types?search=False&business_branch=${ business_branch_id }`);
      setPaymentTypes(res.data.data);
    } catch { }
  };

  const fetchCustomers = async () => {
    try {
      const res = await requester.get(`/customers/list?search=False&business_branch=${ business_branch_id }`);
      setCustomers(res.data.data);
    } catch { }
  };

  const resetReceiptState = () => {
    setIsValidated(false);
    setPaymentsTotal(0);
    setPaymentModesUsed("");
  };

  const handleChangeAmount = useCallback((value, payType) => {
    const total = value.reduce((a, { amount_paid }) => a + parseFloat(amount_paid || 0), 0);
    setPaymentsTotal(total);
    setPaymentModesUsed(value);

    if (payType === true) {
      setIsValidated(parseFloat(total) === parseFloat(invoiceDetails.total_amount));
    } else {
      setIsValidated(parseFloat(total) >= parseFloat(invoiceDetails.total_amount));
    }
  }, [invoiceDetails.total_amount]);

  const handleClickOpen = () => {
    resetReceiptState();
    fetchPaymentTypes();
    setOpen(true);
  };

  const handleClose = () => {
    resetReceiptState();
    setMultiplePayments(false);
    setOpen(false);
  };

  /* ── Mode toggle — resets validation since amounts may no longer satisfy ── */
  const handleChangeMultiplePymentModes = () => {
    setMultiplePayments((prev) => !prev);
    resetReceiptState();
  };

  /* ── Invoice dialog handlers ── */
  const handleClickOpenInvoice = () => {
    fetchCustomers();
    setOpenInvoice(true);
  };

  const handleCloseInvoice = () => {
    setIsValidatedInvoice(false);
    setOpenInvoice(false);
  };

  const handleChangeCustomer = (e) => {
    setCustomer(e.target.value);
    setIsValidatedInvoice(true);
  };

  /* ── Thermal receipt ── */
  const COL = { NAME: 20, QTY: 5, GAP: 1, PRICE: 9, AMOUNT: 10 };

  const col = (str, width, align = "left") => {
    const s = String(str ?? "").slice(0, width);
    return align === "right"
      ? s.padStart(width, " ")
      : s.padEnd(width, " ");
  };

  const safeNum = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  /* FIX: strip trailing ".00" — 7,000.00 → "7,000" | 7,000.50 stays "7,000.50" */
  const fmtAmt = (v) => String(withoutSymbolFormatter(safeNum(v))).replace(/\.00$/, "");

  /* FIX: strip trailing ".0" from qty — 2.0 → "2" | 2.5 stays "2.5" */
  const fmtQty = (v) => String(Number(v).toFixed(1)).replace(/\.0$/, "");

  const buildRowRight = (item) => {
    const qty = fmtQty(item.quantity);
    const price = fmtAmt(item.sale_price);
    const amount = fmtAmt(item.total);

    return (
      col(qty, COL.QTY, "right") +
      " ".repeat(COL.GAP) +
      col(price, COL.PRICE, "right") +
      " ".repeat(COL.GAP) +
      col(amount, COL.AMOUNT, "right")
    );
  };

  const receipt = () => (
    <Printer type="epson" width={48} characterSet="pc437_usa">
      <Image align="center" src={JolilabsLogo} />
      <Br />
      <Text align="center" bold>LOBIAN PHARMACY</Text>
      <Text align="center">+256744399628/ 0789880942</Text>
      <Text align="center">lobianpharmacy1@gmail.com</Text>
      <Br />
      <Line />
      <Text align="center" bold>SALES RECEIPT</Text>
      <Line />
      <Text align="center" bold>
        Receipt Number: {invoiceDetails.receipt?.receipt_number ?? "—"}
      </Text>
      <Text>Date: {moment().format("LLL")}</Text>
      <Text>
        Customer:{" "}
        {invoiceDetails.related_customer
          ? `${ invoiceDetails.related_customer.related_user.first_name } ${ invoiceDetails.related_customer.related_user.last_name }`
          : "Walk-in Customer"}
      </Text>
      <Br />
      <Line />
      <Text size={{ width: 1, height: 1 }}>
        {col("Item", COL.NAME, "left")}
        {"  "}{col("Qty", COL.QTY, "right")}
        {" ".repeat(COL.GAP)}
        {col("Price", COL.PRICE, "right")}
        {" ".repeat(COL.GAP)}
        {col("Amt", COL.AMOUNT, "right")}
      </Text>
      <Line />
      {products.map((item, i) => (
        <Row
          key={i}
          gap={1}
          left={
            <Text>{item.related_product?.product_name}</Text>
          }
          right={
            <Text size={{ width: 1, height: 1 }}>{buildRowRight(item)}</Text>
          }
        />
      ))}
      <Line />
      <Row left={<Text>TOTAL</Text>} right={fmtAmt(invoiceDetails.total_amount)} />
      <Row left={<Text>CASH</Text>} right={fmtAmt(invoiceDetails.receipt?.amount_tendered)} />
      <Row left={<Text>BALANCE</Text>} right={fmtAmt(invoiceDetails.receipt?.balance)} />
      <Br />
      <Line />
      <Text align="center">
        Served by: {invoiceDetails.created_by?.first_name ?? ""}{" "}
        {invoiceDetails.created_by?.last_name ?? ""}
      </Text>
      <Cut />
    </Printer>
  );

  /* ── Printer port cache — reuse across prints to avoid USB enumeration issues ── */
  const portRef = React.useRef(null);
  const printCountRef = React.useRef(0);

  const executePrint = async () => {
    const data = await render(receipt());
    try {

      const shouldResetPort = printCountRef.current >= 3;

      if (shouldResetPort && portRef.current) {
        try { await portRef.current.close(); } catch { }
        portRef.current = null;
        printCountRef.current = 0;

        await new Promise((resolve) => setTimeout(resolve, 800));
      }

      if (!portRef.current) {
        portRef.current = await navigator.serial.requestPort({ filters: [{ usbVendorId: 0x1fc9 }, { usbVendorId: 0x28e9 }] });
      }

      const port = portRef.current;

      // Open only if not already open
      if (!port.readable && !port.writable) {
        await port.open({ baudRate: 9600 });
      }

      const writer = port.writable?.getWriter();
      if (writer) {
        await writer.write(data);
        await writer.ready;
        writer.releaseLock();
      }

      await new Promise((resolve) => setTimeout(resolve, 300));

      printCountRef.current += 1;
    } catch (err) {
      // On any error, clear the cached port so next print gets a fresh one
      portRef.current = null;
      printCountRef.current = 0;
      console.error("Print error:", err);
    }
    navigate("/billing");
  };

  const saveReceipt = async () => {
    setIsSubmittingReceipt(true);
    invoiceDetails.receipt.amount_tendered = paymentsTotal;
    invoiceDetails.receipt.balance = parseFloat(paymentsTotal) - parseFloat(invoiceDetails.total_amount);
    try {
      await requester.post("/billing/receipts", {
        invoice_number: invoiceDetails.invoice_number,
        amount_paid: invoiceDetails.total_amount,
        amount_tendered: paymentsTotal,
        date_created: receiptDate,
        balance: (parseFloat(paymentsTotal) - parseFloat(invoiceDetails.total_amount)).toFixed(2),
        payment_modes: paymentModesUsed,
      });
      executePrint();
    } catch { }
    setIsSubmittingReceipt(false);
  };

  const saveInvoice = async () => {
    setIsSubmittingInvoice(true);
    try {
      await requester.post("/billing/receipts", {
        invoice_number: invoiceDetails.invoice_number,
        amount_paid: 0,
        related_customer: customer,
        date_created: receiptDate,
        amount_tendered: 0,
        balance: 0,
        payment_modes: [],
      });
      navigate("/billing");
    } catch { }
    setIsSubmittingInvoice(false);
  };

  /* ── Derived display values ── */
  const customerName = invoiceDetails.related_customer
    ? `${ invoiceDetails.related_customer.related_user.first_name } ${ invoiceDetails.related_customer.related_user.last_name }`
    : null;

  const createdBy = !loading && invoiceDetails.created_by
    ? `${ invoiceDetails.created_by.first_name } ${ invoiceDetails.created_by.last_name }`
    : "—";

  const productColumns = [
    { label: "Product Name" },
    { label: "Unit" },
    { label: "Batch" },
    { label: "Qty", align: "center" },
    { label: "Unit Price", align: "right" },
    { label: "Discount", align: "right" },
    { label: "Total", align: "right" },
  ];

  /* ── FIX: safe string helpers so numbers like 0 never render as bare children ── */
  const safeStr = (v) => (v != null && v !== "" ? String(v) : "—");
  const safeCurrency = (v) => currencyFormatter(parseFloat(v) || 0);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Breadcrumb + header ── */}
      <Box>
        <Breadcrumbs separator={<ChevronRightIcon sx={{ fontSize: "14px", color: border }} />} sx={{ mb: "0.5rem" }}>
          <Typography
            onClick={() => navigate("/billing")}
            sx={{ fontSize: "12px", color: accent, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", "&:hover": { textDecoration: "underline" } }}
          >
            <ReceiptIcon sx={{ fontSize: "14px" }} /> Sales
          </Typography>
          <Typography sx={{ fontSize: "12px", color: textSec }}>Invoice Details</Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
            <ReceiptIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Invoice Details
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {invoiceDetails.invoice_number ?? "Loading…"}
            </Typography>
          </Box>
          {!loading && (
            <Box sx={{ ml: "auto" }}>
              <StatusPill status={invoiceDetails.status} />
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Main body ── */}
      <Box sx={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "flex-start" }}>

        {/* ── Left column ── */}
        <Box sx={{ flex: "1 1 500px", display: "flex", flexDirection: "column", gap: "1rem" }}>

          {/* Invoice info card */}
          <Box sx={{ backgroundColor: cardBg, border: `1px solid ${ border }`, borderRadius: "12px", overflow: "hidden" }}>
            <Box sx={{ px: "1.25rem", py: "1rem", borderBottom: `1px solid ${ border }`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)" }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>
                Invoice Information
              </Typography>
            </Box>
            <Box sx={{ p: "0.5rem 1.25rem 1rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 2rem" }}>
              <DetailField icon={<TagIcon />} label="Invoice Number" value={safeStr(invoiceDetails.invoice_number)} textPri={textPri} textSec={textSec} accent={accent} />
              {/* FIX: always pass a string to DetailField value */}
              <DetailField icon={<AttachMoneyIcon />} label="Total Amount" value={safeCurrency(invoiceDetails.total_amount)} textPri={textPri} textSec={textSec} accent={accent} />
              <DetailField icon={<CategoryIcon />} label="Order Type" value={safeStr(invoiceDetails.order_type)} textPri={textPri} textSec={textSec} accent={accent} />
              {/* FIX: total_discount could be 0 — format as currency string, never raw number */}
              <DetailField icon={<DiscountIcon />} label="Total Discount" value={safeCurrency(invoiceDetails.total_discount)} textPri={textPri} textSec={textSec} accent={accent} />
              <DetailField icon={<PersonIcon />} label="Customer" value={safeStr(customerName)} textPri={textPri} textSec={textSec} accent={accent} />
              <DetailField icon={<BadgeIcon />} label="Created By" value={safeStr(createdBy)} textPri={textPri} textSec={textSec} accent={accent} />
            </Box>
          </Box>

          {/* Products table */}
          <Box sx={{ backgroundColor: cardBg, border: `1px solid ${ border }`, borderRadius: "12px", overflow: "hidden" }}>
            <Box sx={{ px: "1.25rem", py: "1rem", borderBottom: `1px solid ${ border }`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)" }}>
              {/* FIX: use ternary instead of && so 0 is never rendered as a bare child */}
              <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>
                {`Products${ products.length > 0 ? ` (${ products.length })` : "" }`}
              </Typography>
            </Box>
            <Box sx={{ p: "0.75rem 1rem" }}>
              <ReportTable
                columns={productColumns}
                rows={products}
                loading={loading}
                emptyMessage="No products on this invoice"
                renderRow={(item, index) => (
                  <RRow key={item.id ?? index} index={index}>
                    <RCell>
                      <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                        {item.related_product.product_name}
                      </Typography>
                      {item.related_product.description && (
                        <Typography sx={{ fontSize: "11px", color: textSec, mt: "1px" }}>
                          {item.related_product.description}
                        </Typography>
                      )}
                    </RCell>
                    <RCell>{item.related_product_unit?.related_business_product_unit?.name ?? "—"}</RCell>
                    <RCell>{item.batch ?? "—"}</RCell>
                    <RCell align="center">{Number(item.quantity).toFixed(1)}</RCell>
                    <RCell align="right">{currencyFormatter(parseFloat(item.sale_price))}</RCell>
                    {/* FIX: discount could be 0 — parseFloat(0 || 0) is fine but be explicit */}
                    <RCell align="right">{currencyFormatter(parseFloat(item.discount ?? 0))}</RCell>
                    <RCell align="right">
                      <Typography sx={{ fontSize: "13px", fontWeight: 500, color: accent }}>
                        {currencyFormatter(parseFloat(item.total))}
                      </Typography>
                    </RCell>
                  </RRow>
                )}
              />
            </Box>
          </Box>
        </Box>

        {/* ── Right column ── */}
        <Box sx={{ flex: "0 0 260px", display: "flex", flexDirection: "column", gap: "1rem" }}>

          {/* Actions card */}
          {!loading && (
            <Box sx={{ backgroundColor: cardBg, border: `1px solid ${ border }`, borderRadius: "12px", overflow: "hidden" }}>
              <Box sx={{ px: "1.25rem", py: "1rem", borderBottom: `1px solid ${ border }`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)" }}>
                <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>
                  Actions
                </Typography>
              </Box>
              <Box sx={{ p: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {!invoiceDetails.status ? (
                  <>
                    <ActionBtn icon={<SendIcon sx={{ fontSize: "16px" }} />} onClick={handleClickOpen}>
                      Create Receipt
                    </ActionBtn>
                    <ActionBtn icon={<SendIcon sx={{ fontSize: "16px" }} />} variant="ghost" onClick={handleClickOpenInvoice}>
                      Create Invoice
                    </ActionBtn>
                  </>
                ) : (
                  <>
                    <ActionBtn icon={<PrintIcon sx={{ fontSize: "16px" }} />} onClick={executePrint}>
                      Print POS Receipt
                    </ActionBtn>
                    <PDFDownloadLink
                      document={<SalesReceiptPdf viewData={invoiceDetails} printedBy={`${ user.first_name } ${ user.last_name }`} />}
                      fileName={`Sales Receipt #${ invoiceDetails.receipt?.receipt_number }`}
                      style={{ textDecoration: "none" }}
                    >
                      <ActionBtn icon={<DownloadIcon sx={{ fontSize: "16px" }} />} variant="ghost">
                        Download Receipt
                      </ActionBtn>
                    </PDFDownloadLink>
                  </>
                )}

                {!invoiceDetails.status && !invoiceDetails.is_invoice && (
                  <PDFDownloadLink
                    document={<SalesBillPdf viewData={invoiceDetails} printedBy={`${ user.first_name } ${ user.last_name }`} />}
                    fileName={`Sales Bill #${ invoiceDetails.invoice_number }`}
                    style={{ textDecoration: "none" }}
                  >
                    <ActionBtn icon={<DownloadIcon sx={{ fontSize: "16px" }} />} variant="ghost">
                      Download Bill
                    </ActionBtn>
                  </PDFDownloadLink>
                )}

                {invoiceDetails.is_invoice && (
                  <PDFDownloadLink
                    document={<SalesInvoicePdf viewData={invoiceDetails} printedBy={`${ user.first_name } ${ user.last_name }`} />}
                    fileName={`Sales Invoice #${ invoiceDetails.invoice_number }`}
                    style={{ textDecoration: "none" }}
                  >
                    <ActionBtn icon={<DownloadIcon sx={{ fontSize: "16px" }} />} variant="ghost">
                      Download Invoice
                    </ActionBtn>
                  </PDFDownloadLink>
                )}
              </Box>
            </Box>
          )}

          {/* Summary card */}
          <Box sx={{ backgroundColor: cardBg, border: `1px solid ${ border }`, borderRadius: "12px", overflow: "hidden" }}>
            <Box sx={{ px: "1.25rem", py: "1rem", borderBottom: `1px solid ${ border }`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)" }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>
                Summary
              </Typography>
            </Box>
            {/* FIX: always produce string values — 0 formatted as currency, never raw number */}
            {[
              { label: "Total Amount", value: safeCurrency(invoiceDetails.total_amount), highlight: true },
              { label: "Total Discount", value: safeCurrency(invoiceDetails.total_discount), highlight: false },
            ].map(({ label, value, highlight }, i, arr) => (
              <Box key={label} sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                px: "1.25rem", py: "0.7rem",
                borderBottom: i < arr.length - 1 ? `1px solid ${ border }` : "none",
                backgroundColor: highlight
                  ? (isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.04)")
                  : "transparent",
              }}>
                <Typography sx={{ fontSize: "12px", color: textSec }}>{label}</Typography>
                <Typography sx={{ fontSize: highlight ? "16px" : "13px", fontWeight: highlight ? 600 : 500, color: highlight ? accent : textPri }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Receipt dialog ── */}
      {open && (
        <FormProviderCreateReceipt>
          <NewDialog
            open={open}
            handleClose={handleClose}
            enableActions={true}
            title="Create Receipt"
            buttonText="Create Receipt"
            handleSubmit={saveReceipt}
            isSubmitting={isSubmittingReceipt}
            isSubmittingText="Creating receipt, please wait…"
            textStatus={!isValidated}
            text="Please ensure you receive and enter the correct amount."
            isValidated={isValidated}
            content={
              <ReceiptConfirmView
                amountPaid={amountPaid}
                total={invoiceDetails.total_amount}
                paymentModes={paymentTypes}
                paymentsTotal={paymentsTotal}
                handleChangeAmount={handleChangeAmount}
                multiplePayments={multiplePayments}
                handleChangeMultiplePymentModes={handleChangeMultiplePymentModes}
                receiptDate={receiptDate}
                handleChangePaymentDate={(e) => setReceiptDate(e.target.value)}
              />
            }
          />
        </FormProviderCreateReceipt>
      )}

      {/* ── Invoice dialog ── */}
      {openInvoice && (
        <NewDialog
          open={openInvoice}
          handleClose={handleCloseInvoice}
          enableActions={true}
          title="Create Customer Invoice"
          buttonText="Create Customer Invoice"
          handleSubmit={saveInvoice}
          isSubmitting={isSubmittingInvoice}
          isSubmittingText="Creating invoice, please wait…"
          textStatus={!isValidatedInvoice}
          text="Please ensure you select the correct customer."
          isValidated={isValidatedInvoice}
          content={
            <InvoiceCreateView
              total={invoiceDetails.total_amount}
              customers={customers}
              handleChangeCustomer={handleChangeCustomer}
              customer={customer}
            />
          }
        />
      )}
    </Box>
  );
};

export default InvoiceDetailsView;